import { baseApi } from "../../../../app/api";
import { store } from "../../../../app/store";
import {
  RawWPRestProductCategory,
  RawWPRestProductCategoriesResponse,
  RawWPRestProductTag,
  RawWPRestProductTagsResponse,
  RawWPRestBrand,
  RawWPRestBrandsResponse,
} from "../../../../types/index";

import type {
  TransformedWPCategory,
  GetWPCategoriesResult,
  TransformedWPTag,
  GetWPTagsResult,
  TransformedWPBrand,
  GetBrandsResult,
} from "../types/productTaxonomyApiTypes";

// Get the WordPress REST API base URL from theme variables or use default fallback
const WP_REST_API_BASE =
  window.themeVariables?.wpRestApiBase || "/wp-json/wp/v2";

// --- Transformation Functions ---

// Define the taxonomy API slice by injecting endpoints into the baseApi setup
export const taxonomyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Endpoint to fetch product categories from WordPress REST API
     * Provides a rich dataset including translations and taxonomy data
     * @returns Object containing an array of transformed WP categories
     */
    getCategories: builder.query<GetWPCategoriesResult, void>({
      query: () => ({
        url: `${WP_REST_API_BASE}/product_cat`,
        params: {
          per_page: 100,
        },
      }),
      transformResponse: (
        response: RawWPRestProductCategoriesResponse
      ): GetWPCategoriesResult => {
        // Transform the category data and create an ID-indexed object
        const categoriesById = response.reduce((acc, category) => {
          acc[category.id] = transformCategoryData(category);
          return acc;
        }, {} as Record<number, TransformedWPCategory>);

        // Return the categories indexed by ID
        return { categories: categoriesById };
      },
      providesTags: (result) =>
        result?.categories
          ? [
              ...Object.keys(result.categories).map((id) => ({
                type: "Category" as const,
                id,
              })),
              { type: "Category" as const, id: "LIST" },
            ]
          : [{ type: "Category" as const, id: "LIST" }],
    }),

    /**
     * Endpoint to fetch product tags from WordPress REST API
     * Provides a rich dataset including taxonomy data
     * @returns Object containing an array of transformed WP tags
     */
    getTags: builder.query<GetWPTagsResult, void>({
      query: () => ({
        url: `${WP_REST_API_BASE}/product_tag`,
        params: {
          per_page: 100,
        },
      }),
      transformResponse: (
        response: RawWPRestProductTagsResponse
      ): GetWPTagsResult => {
        // Transform the tag data and create an ID-indexed object
        const tagsById = response.reduce((acc, tag) => {
          acc[tag.id] = transformTagData(tag);
          return acc;
        }, {} as Record<number, TransformedWPTag>);

        // Return the tags indexed by ID
        return { tags: tagsById };
      },
      providesTags: (result) =>
        result?.tags
          ? [
              ...Object.keys(result.tags).map((id) => ({
                type: "Tag" as const,
                id,
              })),
              { type: "Tag" as const, id: "LIST" },
            ]
          : [{ type: "Tag" as const, id: "LIST" }],
    }),

    /**
     * Fetches product brands from the WordPress REST API.
     * Uses /wp/v2/product_brand endpoint.
     */
    getBrands: builder.query<GetBrandsResult, void>({
      query: () => ({
        // Target the WordPress REST API endpoint for product_brand taxonomy
        url: `${WP_REST_API_BASE}/product_brand`,
        params: { per_page: 100, orderby: "name", order: "asc" }, // Fetch up to 100 brands, order by name
      }),
      transformResponse: (
        response: RawWPRestBrandsResponse
      ): GetBrandsResult => {
        const rawBrands: RawWPRestBrand[] = Array.isArray(response)
          ? response
          : [];

        // Create brands indexed by ID for consistency with the rest of the app
        const brandsById = rawBrands.reduce((acc, brand) => {
          const transformedBrand: TransformedWPBrand = {
            id: brand.id,
            name: brand.name,
            slug: brand.slug,
            parent: brand.parent,
            description: brand.description,
            count: brand.count,
          };
          acc[brand.id] = transformedBrand;
          return acc;
        }, {} as GetBrandsResult["brands"]);

        return { brands: brandsById };
      },
      providesTags: (result) =>
        result?.brands
          ? [
              ...Object.keys(result.brands).map((id) => ({
                type: "Brand" as const,
                id,
              })),
              { type: "Brand" as const, id: "LIST" },
            ]
          : [{ type: "Brand" as const, id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

/**
 * Transforms a WordPress REST API category into the application's TransformedWPCategory structure
 * Applies translations if available for the current language
 * @param category - Raw WordPress REST API category data
 * @returns Transformed category data
 */
const transformCategoryData = (
  category: RawWPRestProductCategory
): TransformedWPCategory => {
  // Get language information from Redux store
  const state = store.getState();
  const currentLanguage = state.language.currentLanguage;
  const defaultLanguage = state.language.defaultLanguage;

  // Default transformed data
  const transformedCategory = {
    id: category.id,
    name: category.name,
    slug: category.slug,
    parent: category.parent,
    count: category.count,
    description: category.description,
    link: category.link,
    taxonomy: category.taxonomy,
  };

  // Apply translations if:
  // 1. Current language is not the default language
  // 2. Translations object exists
  // 3. Translations for the current language exist
  if (
    currentLanguage !== defaultLanguage &&
    category.translations &&
    typeof category.translations === "object" &&
    category.translations[currentLanguage]
  ) {
    const translation = category.translations[currentLanguage];

    // Override name with translated version ONLY if it exists and is not empty
    // Otherwise keep the default language name as fallback
    if (translation.name && translation.name.trim() !== "") {
      transformedCategory.name = translation.name;
    }

    // Override description with translated version ONLY if it exists and is not empty
    // Otherwise keep the default language description as fallback
    if (translation.description && translation.description.trim() !== "") {
      transformedCategory.description = translation.description;
    }
  }

  return transformedCategory;
};

/**
 * Transforms a WordPress REST API tag into the application's TransformedWPTag structure
 * Applies translations if available for the current language
 * @param tag - Raw WordPress REST API tag data
 * @returns Transformed tag data
 */
const transformTagData = (tag: RawWPRestProductTag): TransformedWPTag => {
  // Get language information from Redux store
  const state = store.getState();
  const currentLanguage = state.language.currentLanguage;
  const defaultLanguage = state.language.defaultLanguage;

  // Default transformed data
  const transformedTag = {
    id: tag.id,
    name: tag.name,
    slug: tag.slug,
    count: tag.count,
    description: tag.description,
    link: tag.link,
    taxonomy: tag.taxonomy,
    meta: tag.meta,
    acf: tag.acf,
  };

  // Apply translations if:
  // 1. Current language is not the default language
  // 2. Translations object exists
  // 3. Translations for the current language exist
  if (
    currentLanguage !== defaultLanguage &&
    tag.translations &&
    typeof tag.translations === "object" &&
    tag.translations[currentLanguage]
  ) {
    const translation = tag.translations[currentLanguage];

    // Override name with translated version ONLY if it exists and is not empty
    // Otherwise keep the default language name as fallback
    if (translation.name && translation.name.trim() !== "") {
      transformedTag.name = translation.name;
    }

    // Override description with translated version ONLY if it exists and is not empty
    // Otherwise keep the default language description as fallback
    if (translation.description && translation.description.trim() !== "") {
      transformedTag.description = translation.description;
    }
  }

  return transformedTag;
};

// Export the auto-generated hooks from the taxonomyApi slice for use in components
export const {
  useGetCategoriesQuery,
  useGetTagsQuery,
  useGetBrandsQuery,
  util: { getRunningQueriesThunk },
} = taxonomyApi;

// Export the endpoint definitions themselves for potential use in SSR, testing, or manual dispatching
export const { getCategories, getTags, getBrands } = taxonomyApi.endpoints;
